/**
 * usePackageValidation Hook
 *
 * RTKQ-based validation state with selectFromResult for granular subscriptions.
 * Components only re-render when validation data actually changes.
 */
import { useMatter } from '@/hooks/useMatter';
import { useCorrespondencePackageValidateAllQuery } from '@/services/api/correspondenceService';
import type { PackageValidationResponse } from '@/services/api/generated/correspondenceService';
import type { MatterFlattenedContext } from '@/services/api/matterFlattenedContextService';

import type { PackageValidationStatus } from '../types';
import {
  normalizeApiMissingFields,
  type TemplateField,
} from '../utils/fieldValidation';

// ============================================================================
// Types
// ============================================================================

export interface UsePackageValidationOptions {
  packageId: string | undefined;
  /** Skip fetching (e.g., while package is being created) */
  skip?: boolean;
}

export interface UsePackageValidationResult {
  // Validation status
  validationStatus: PackageValidationStatus;
  matterContext: MatterFlattenedContext | undefined;

  // Loading state
  isLoading: boolean;
  isFetching: boolean;
}

// ============================================================================
// Hook
// ============================================================================

/** Compute validation status from API response */
function computeValidationStatus(
  data: PackageValidationResponse | undefined,
): PackageValidationStatus {
  if (!data) {
    return {
      componentCount: 0,
      hasValidationIssues: false,
      hasMissingFields: false,
      missingFieldsCount: 0,
      componentResults: [],
    };
  }

  const componentResults = data.components.map((c) => ({
    componentId: c.component_id,
    isValid: c.is_valid,
    missingFields: normalizeApiMissingFields(c.missing_fields),
    missingCount: c.missing_fields_count,
    templateFields: c.template_fields as TemplateField[] | undefined,
    // annotation_fields for merge documents - cast since types may not be generated yet
    annotationFields: (c as unknown as { annotation_fields?: unknown[] })
      .annotation_fields as
      | Array<{
          field_id: string;
          field_name: string;
          field_source: string;
          context_key: string | null;
          current_value: string | null;
          value_source: 'context' | 'override' | null;
          is_missing: boolean;
        }>
      | undefined,
  }));

  const missingFieldsCount = componentResults.reduce(
    (sum, r) => sum + r.missingCount,
    0,
  );

  return {
    componentCount: data.total_components,
    hasValidationIssues: data.valid_components < data.total_components,
    hasMissingFields: missingFieldsCount > 0,
    missingFieldsCount,
    componentResults,
  };
}

export function usePackageValidation({
  packageId,
  skip = false,
}: UsePackageValidationOptions): UsePackageValidationResult {
  const { matterId } = useMatter();

  // Use selectFromResult to compute validation status in selector
  // This minimizes re-renders - only triggers when computed values change
  return useCorrespondencePackageValidateAllQuery(
    { id: packageId || '', matterId },
    {
      skip: skip || !packageId,
      selectFromResult: ({ data, isLoading, isFetching }) => ({
        validationStatus: computeValidationStatus(data),
        matterContext: data?.matter_context as
          | MatterFlattenedContext
          | undefined,
        isLoading,
        isFetching,
      }),
    },
  );
}
